import { useState } from 'react'
import { Broadcast } from '@phosphor-icons/react'
import { DataTable, EmptyState, Modal, Spinner, type Column } from '@/ui'
import { errorMessage } from '../api/core'
import {
  LIVE_REFRESH_MS,
  useOpenDeliveries,
  useOpenDelivery,
  type OpenDeliveryRow,
  type OpenDeliveryStatus,
} from '../api/openDeliveries'
import { useVillages } from '../api/reference'
import { SelectFilter } from '../components/FilterBar'
import { ConsolePage } from '../components/Layout'
import { Code, Kg, When } from '../components/bits'
import { useUrlState } from '../lib/useUrlState'

/** Where the operator has got to with this farmer, in the gate's own order. */
const STEP_LABEL: Record<string, string> = {
  details: 'Crop and questions',
  check: 'Checking bags',
  moisture: 'Moisture',
  price: 'Price',
  tag: 'Tagging and weighing',
  sample: 'Sample',
  slip: 'Slip and agreement',
  close: 'Closing',
}

const STATUS_STYLE: Record<OpenDeliveryStatus, { label: string; className: string }> = {
  OPEN: { label: 'Open', className: 'bg-warn/15 text-warn' },
  COMPLETED: { label: 'Completed', className: 'bg-ok/15 text-ok' },
  CANCELLED: { label: 'Cancelled', className: 'bg-muted/15 text-muted' },
}

function StatusChip({ status }: { status: OpenDeliveryStatus }) {
  const s = STATUS_STYLE[status]
  return (
    <span className={`rounded-full px-2 py-0.5 text-[12px] font-semibold ${s.className}`}>
      {s.label}
    </span>
  )
}

/**
 * Farmers open at the gate right now, from the backups phones send as each step is done. A view
 * only: the purchase becomes a lot and a payout when the phone completes it, as before. Refreshes
 * every 30 s while the page is in front.
 */
export function OpenAtGatePage() {
  const villages = useVillages()
  const [f, setF] = useUrlState({
    groupId: '',
    status: 'OPEN' as OpenDeliveryStatus | 'ALL',
  })
  const q = useOpenDeliveries({ groupId: f.groupId || undefined, status: f.status })
  const [picked, setPicked] = useState<OpenDeliveryRow | null>(null)

  const columns: Column<OpenDeliveryRow>[] = [
    { id: 'status', header: 'Status', cell: (r) => <StatusChip status={r.status} /> },
    {
      id: 'farmer',
      header: 'Farmer',
      cell: (r) => (
        <span className="flex flex-col">
          <span className="font-semibold">{r.farmerName ?? '—'}</span>
          {r.farmerCode && <Code>{r.farmerCode}</Code>}
        </span>
      ),
    },
    { id: 'crop', header: 'Crop', cell: (r) => r.cropName ?? '—', hideBelow: 'sm' },
    {
      id: 'step',
      header: 'Step',
      cell: (r) => (r.step ? (STEP_LABEL[r.step] ?? r.step) : '—'),
    },
    { id: 'bags', header: 'Bags', align: 'right', cell: (r) => r.bags },
    { id: 'kg', header: 'Gross', align: 'right', cell: (r) => <Kg grams={r.grossG} /> },
    {
      id: 'where',
      header: 'Village · phone',
      cell: (r) => (
        <span className="flex flex-col">
          <span>{r.villageName ?? '—'}</span>
          <span className="text-[12px] text-muted">
            {[r.devicePrefix, r.actorName].filter(Boolean).join(' · ')}
          </span>
        </span>
      ),
      hideBelow: 'md',
    },
    {
      id: 'updated',
      header: 'Last change',
      cell: (r) => <When at={r.phoneUpdatedAt} relative />,
    },
  ]

  return (
    <ConsolePage
      eyebrow="Farm gate"
      title="Open at the gate"
      description="Farmers the phones are buying from right now, as of each phone's last backup (sent a few seconds after each step while it has network). A farmer becomes a lot and a payout only when the phone completes the purchase."
    >
      <DataTable<OpenDeliveryRow>
        caption="Open at the gate"
        columns={columns}
        rows={q.data ?? []}
        getRowId={(r) => r.deliveryUlid}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={setPicked}
        pageSize={100}
        filters={
          <>
            <SelectFilter
              label="Village"
              value={f.groupId}
              onChange={(v) => setF({ groupId: v })}
              options={(villages.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
              allLabel="All villages"
            />
            <SelectFilter<OpenDeliveryStatus | 'ALL'>
              label="Show"
              value={f.status === 'ALL' ? '' : f.status}
              onChange={(v) => setF({ status: v || 'ALL' })}
              options={[
                { value: 'OPEN', label: 'Open now' },
                { value: 'COMPLETED', label: 'Completed' },
                { value: 'CANCELLED', label: 'Cancelled' },
              ]}
              allLabel="All (last 7 days)"
            />
            <span className="ml-auto self-end text-[12px] text-muted">
              Updates every {LIVE_REFRESH_MS / 1000} s
            </span>
          </>
        }
        empty={
          <EmptyState
            icon={<Broadcast />}
            title={f.status === 'OPEN' ? 'No farmer open at the gate' : 'Nothing here'}
            description="A farmer shows here a few seconds after the phone starts buying from him, while the phone has network."
          />
        }
      />
      {picked && <OpenDeliveryModal row={picked} onClose={() => setPicked(null)} />}
    </ConsolePage>
  )
}

function OpenDeliveryModal({ row, onClose }: { row: OpenDeliveryRow; onClose: () => void }) {
  const q = useOpenDelivery(row.deliveryUlid)
  const live = (q.data?.bags ?? []).filter((b) => !b.withdrawn)
  return (
    <Modal
      open
      onClose={onClose}
      title={`${row.farmerName ?? 'Farmer'}${row.farmerCode ? ` · ${row.farmerCode}` : ''}`}
      description={`${row.villageName ?? ''}${row.cropName ? ` · ${row.cropName}` : ''}${row.mainLotCode ? ` · lot ${row.mainLotCode}` : ''}`}
    >
      {q.isLoading ? (
        <Spinner />
      ) : q.isError ? (
        <p className="text-bad">{errorMessage(q.error)}</p>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3 text-[14px]">
            <StatusChip status={q.data?.status ?? row.status} />
            <span>
              Moisture: <b>{q.data?.moisturePct != null ? `${q.data.moisturePct}%` : '—'}</b>
            </span>
            <span>
              Last change: <When at={q.data?.phoneUpdatedAt ?? row.phoneUpdatedAt} relative />
            </span>
          </div>
          {live.length === 0 ? (
            <p className="m-0 text-muted">No bag tagged yet.</p>
          ) : (
            <table className="w-full text-[14px]">
              <thead>
                <tr className="text-left text-muted">
                  <th className="py-1">#</th>
                  <th>Tag</th>
                  <th className="text-right">Gross</th>
                </tr>
              </thead>
              <tbody>
                {live.map((b, i) => (
                  <tr key={b.id} className="border-t border-rule">
                    <td className="py-1">
                      {i + 1}
                      {b.red ? ' · red tie' : ''}
                    </td>
                    <td>
                      <Code>{b.tag ?? '—'}</Code>
                    </td>
                    <td className="text-right">
                      <Kg grams={b.grossG} />
                      {b.weighings > 1 ? (
                        <span className="ml-1 text-[12px] text-muted">(retyped)</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="m-0 text-[12px] text-muted">
            From the phone’s backup. Weights may still change until the farmer agrees and the phone
            completes the purchase.
          </p>
        </div>
      )}
    </Modal>
  )
}
